import apiService, { getToken } from '../services/api';
import { ORG_HEADER, orgHeaderFor } from './orgGuard';
import type { CallCardData, Voicemail } from '../types/sales';

/**
 * The Sales module's incoming-call stream (GET /api/sales/stream,
 * HHNodeServer salesStreamService): server-sent events read with fetch,
 * because EventSource cannot send the Authorization header.
 *
 * The server closes each stream after a few minutes (Cloud Run ends any
 * request at its timeout) and this opens the next one. A refused stream
 * (expired token) asks the API something cheap, which refreshes the token,
 * and tries again. Out of signal, it waits longer each time, up to a minute.
 */

export interface SalesStreamHandlers {
  onReady?: (ringing: CallCardData[]) => void;
  onCall?: (call: CallCardData) => void;
  onVoicemail?: (vm: Voicemail) => void;
}

/** Splits an event-stream body into its events. Kept apart from fetch for its test. */
export function parseEvents(buffer: string): { events: { event: string; data: string }[]; rest: string } {
  const events: { event: string; data: string }[] = [];
  let rest = buffer.replace(/\r\n/g, '\n');
  let i: number;
  while ((i = rest.indexOf('\n\n')) >= 0) {
    const block = rest.slice(0, i);
    rest = rest.slice(i + 2);
    let event = 'message';
    const data: string[] = [];
    for (const line of block.split('\n')) {
      if (line.startsWith(':')) continue;
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) data.push(line.slice(5).trimStart());
    }
    if (data.length) events.push({ event, data: data.join('\n') });
  }
  return { events, rest };
}

/** Opens the stream and keeps it open until the returned function is called. */
export function openSalesStream(handlers: SalesStreamHandlers): () => void {
  let stopped = false;
  let controller: AbortController | null = null;
  let wait = 1000;

  const dispatch = (event: string, data: string) => {
    let value: unknown;
    try { value = JSON.parse(data); } catch { return; }
    if (event === 'ready') handlers.onReady?.((value as { ringing?: CallCardData[] }).ringing ?? []);
    else if (event === 'call') handlers.onCall?.(value as CallCardData);
    else if (event === 'voicemail') handlers.onVoicemail?.(value as Voicemail);
  };

  const run = async () => {
    while (!stopped) {
      controller = new AbortController();
      try {
        const headers: Record<string, string> = { Accept: 'text/event-stream' };
        const token = getToken();
        if (token) headers.Authorization = `Bearer ${token}`;
        const org = orgHeaderFor('/sales/stream');
        if (org) headers[ORG_HEADER] = org;
        const res = await fetch(`${apiService.getBaseURL()}/sales/stream`, {
          headers, signal: controller.signal, credentials: 'include', cache: 'no-store',
        });
        if (res.status === 401) {
          // Any ordinary request refreshes the token through the API client.
          await apiService.get('/sales/settings').catch(() => {});
          throw new Error('refused');
        }
        if (res.status === 403 || res.status === 404) return; // no Sales seat here: stop asking
        if (!res.ok || !res.body) throw new Error(`stream ${res.status}`);
        wait = 1000;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parsed = parseEvents(buffer);
          buffer = parsed.rest;
          parsed.events.forEach((e) => dispatch(e.event, e.data));
        }
      } catch {
        if (stopped) return;
        await new Promise((r) => setTimeout(r, wait));
        wait = Math.min(wait * 2, 60000);
      }
    }
  };

  run();
  return () => {
    stopped = true;
    controller?.abort();
  };
}
